import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import EloBadge from "@/components/EloBadge";
import { playingSince } from "@/lib/playingSince";
import Avatar from "@/components/Avatar";
import Avatar2D from "@/components/Avatar2D";
import PlayerLink from "@/components/PlayerLink";
import FriendButton from "@/components/FriendButton";
import ChallengeButton from "@/components/ChallengeButton";
import ProfileAwards from "@/components/ProfileAwards";
import { characterFor } from "@/lib/avatarConfig";
import { ArrowLeft } from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const BOARD_LABEL = {
  boardgame: "Original",
  boardgame2: "Fortress",
  fortress2: "Fortress 2.0",
  infinity: "Infinity (old)",
  infinity2: "Infinity",
  boardgame3: "Classic",
  boardgame4: "Firefly (old)",
  firefly2: "Firefly",
  boardgame5: "The Wave",
  bridges: "Bridges (old)",
  bridges_trial: "Bridges (short)",
  bridges2: "Bridges",
  bridges3: "Bridges 2.0",
  mini: "Mini",
};

function EloTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-xl border border-border bg-background px-3 py-2 text-xs">
      <div className="font-bold">{p.elo} Elo</div>
      <div className="text-muted-foreground">
        {p.game === 0 ? "Start" : p.date ? new Date(p.date).toLocaleDateString() : "Now"}
      </div>
    </div>
  );
}

// Another player's public profile: raider, Elo and how it changed, record
// and games on now to watch, finished games to step through. No email or
// other private details.
export default function PlayerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user?.id && id === user.id) {
      navigate("/profile", { replace: true });
      return;
    }
    let live = true;
    setP(null);
    setError("");
    api
      .get(`/players/${id}`)
      .then(({ data }) => live && setP(data))
      .catch((err) => live && setError(
        err?.response?.status === 404 ? "Player not found." : formatApiError(err?.response?.data?.detail),
      ));
    return () => { live = false; };
  }, [id, user?.id, navigate]);

  const back = (
    <button
      type="button"
      onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/lobby"))}
      className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
      data-testid="player-back"
    >
      <ArrowLeft size={14} weight="bold" /> Back
    </button>
  );

  if (error || !p) {
    return (
      <main className="max-w-7xl mx-auto px-4 md:px-10 py-10" data-testid="player-page">
        {back}
        <p className="text-muted-foreground text-sm">{error || "Loading…"}</p>
      </main>
    );
  }

  const winRate = p.matches_played ? Math.round(((p.wins || 0) / p.matches_played) * 100) : 0;
  const history = p.elo_history || [];

  return (
    <main className="max-w-7xl mx-auto px-4 md:px-10 py-8 md:py-12" data-testid="player-page">
      {back}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="md:col-span-1 bg-surface border border-border rounded-2xl p-8">
          <div className="-mx-8 -mt-8 mb-6 rounded-t-2xl overflow-hidden bg-gradient-to-b from-brand/10 to-transparent">
            <Avatar2D character={characterFor(p.id, p.avatar3d)} pose="idle" className="h-56 mx-auto pt-4 aspect-[240/256]" />
          </div>
          <ProfileAwards awards={p.awards} />
          <div className="flex items-center gap-4">
            <Avatar
              userId={p.id}
              username={p.username}
              version={p.avatar_v ?? null}
              className="w-14 h-14 text-xl font-display font-black"
            />
            <h1 className="font-display text-3xl font-black tracking-tight break-words min-w-0" data-testid="player-name">
              {p.username}
            </h1>
          </div>
          <div className="mt-5">
            <EloBadge elo={p.elo_rating || 1200} size="lg" />
            <p className="mt-3 text-sm text-muted-foreground" data-testid="profile-record">
              {p?.matches_played || 0} matches · {p?.wins || 0}W / {p?.losses || 0}L
            </p>
            {playingSince(p) && (
              <p className="mt-1 text-sm text-muted-foreground" data-testid="playing-since">
                Playing since {playingSince(p)}
              </p>
            )}
          </div>
          <div className="mt-5 flex flex-wrap items-start gap-2">
            <ChallengeButton username={p.username} />
            {/* Friends are for full accounts on both sides (guests.py). */}
            {!user?.is_guest && !p.is_guest && <FriendButton userId={p.id} username={p.username} />}
          </div>
        </div>

        <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3 content-start">
          {[
            { label: "Matches", value: p.matches_played || 0 },
            { label: "Wins", value: p.wins || 0 },
            { label: "Losses", value: p.losses || 0, note: p.draws ? `${p.draws} draw${p.draws === 1 ? "" : "s"}` : null },
            { label: "Win rate", value: `${winRate}%` },
          ].map((s) => (
            <div key={s.label} className="bg-surface border border-border rounded-2xl p-5">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{s.label}</div>
              <div className="font-display text-3xl font-black mt-2">{s.value}</div>
              {s.note && <div className="text-xs text-muted-foreground mt-1">{s.note}</div>}
            </div>
          ))}

          <div className="col-span-2 md:col-span-4 bg-surface border border-border rounded-2xl p-6">
            <h3 className="font-display text-xl font-bold tracking-tight mb-3">Elo over time</h3>
            {history.length <= 1 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No rated games yet.</p>
            ) : (
              <div className="h-56" data-testid="player-elo-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={history} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="game" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={["auto", "auto"]} />
                    <Tooltip content={<EloTip />} />
                    <Line
                      type="monotone"
                      dataKey="elo"
                      stroke="hsl(var(--brand))"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "hsl(var(--brand))" }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>

      {p.live_games?.length > 0 && (
        <div className="bg-surface border border-border rounded-2xl p-6 overflow-x-auto mb-6" data-testid="player-live-games">
          <h3 className="font-display text-xl font-bold tracking-tight mb-4">Current games</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground text-xs uppercase tracking-[0.15em]">
                <th className="font-bold py-2">Opponent</th>
                <th className="font-bold py-2">Board</th>
                <th className="font-bold py-2">Turn</th>
                <th className="font-bold py-2 hidden sm:table-cell">Last move</th>
                <th className="font-bold py-2"></th>
              </tr>
            </thead>
            <tbody>
              {p.live_games.map((g) => (
                <tr key={g.match_id} className="border-t border-border" data-testid={`player-live-${g.match_id}`}>
                  <td className="py-3 pr-2 font-medium">
                    <PlayerLink id={g.opp_id}>{g.opp_username || "Player"}</PlayerLink>
                  </td>
                  <td className="py-3 pr-2 text-muted-foreground">{BOARD_LABEL[g.layout] || "—"}</td>
                  <td className="py-3 pr-2 whitespace-nowrap">
                    <span className="text-xs uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-brand/15 text-brand">
                      Live
                    </span>
                    <span className="ml-2 text-xs font-semibold text-muted-foreground">{g.turn} of 15</span>
                  </td>
                  <td className="py-3 pr-2 text-muted-foreground text-xs hidden sm:table-cell">
                    {g.last_move_at ? new Date(g.last_move_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-3 text-right">
                    <button
                      onClick={() => navigate(`/watch/${g.match_id}`, { state: { back: true } })}
                      className="text-xs font-bold uppercase tracking-wider text-brand hover:underline whitespace-nowrap"
                      data-testid={`player-watch-${g.match_id}`}
                    >
                      Watch
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-surface border border-border rounded-2xl p-6 overflow-x-auto">
        <h3 className="font-display text-xl font-bold tracking-tight mb-4">Past games</h3>
        {p.games.length === 0 ? (
          <p className="text-muted-foreground text-sm">No finished games yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground text-xs uppercase tracking-[0.15em]">
                <th className="font-bold py-2">Opponent</th>
                <th className="font-bold py-2">Board</th>
                <th className="font-bold py-2">Result</th>
                <th className="font-bold py-2 hidden sm:table-cell">Date</th>
                <th className="font-bold py-2"></th>
              </tr>
            </thead>
            <tbody>
              {p.games.map((g) => (
                <tr key={g.match_id} className="border-t border-border" data-testid={`player-game-${g.match_id}`}>
                  <td className="py-3 pr-2 font-medium">
                    <PlayerLink id={g.opp_id}>{g.opp_username || "Player"}</PlayerLink>
                    {g.is_bot && <span className="ml-1.5 text-xs text-muted-foreground">(bot)</span>}
                  </td>
                  <td className="py-3 pr-2 text-muted-foreground">{BOARD_LABEL[g.layout] || "—"}</td>
                  <td className="py-3 pr-2 whitespace-nowrap">
                    <span
                      className={`text-xs uppercase tracking-wider font-bold px-2.5 py-1 rounded-full ${
                        g.result === "win"
                          ? "bg-[hsl(var(--chart-4))]/20 text-[hsl(var(--chart-4))]"
                          : g.result === "loss"
                          ? "bg-destructive/15 text-destructive"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {g.result}
                    </span>
                    {g.elo_change != null && g.elo_change !== 0 && (
                      <span className="ml-2 text-xs font-semibold text-muted-foreground">
                        {g.elo_change > 0 ? `+${g.elo_change}` : g.elo_change}
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-2 text-muted-foreground text-xs hidden sm:table-cell">
                    {g.completed_at ? new Date(g.completed_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-3 text-right">
                    {g.has_moves && (
                      <button
                        onClick={() => navigate(`/watch/${g.match_id}`, { state: { back: true } })}
                        className="text-xs font-bold uppercase tracking-wider text-brand hover:underline whitespace-nowrap"
                        data-testid={`player-step-${g.match_id}`}
                      >
                        Step through
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}
